import { Switch } from '../../../../components';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    BulletList,
    Card,
    Exercise,
    LabelBody,
    LabelBodyHeadline,
    LabelDisplay1,
    LabelDisplay2,
    LabelTelescope,
    ProgressBar,
    SecondaryButton,
    Spacer,
    SwirlyBox,
    Textarea,
} from '../../components';
import { Module2S1Exercise1Illustration1 } from '../../icons';
import { ExerciseStatus, Question } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseViewProps } from '../exercises';

const page1WhenIsItHardKey = 'exercise211Page1WhenIsItHardKey';
const page1TypicalActionsKey = 'exercise211Page1TypicalActionsKey';
export const page2SituationsToPracticeKey =
    'exercise211Page2SituationsToPracticeKey';
export const dataKeys = [
    page1WhenIsItHardKey,
    page1TypicalActionsKey,
    page2SituationsToPracticeKey,
];
export const questions: Question[] = [
    createQuestionFromConfiguration({
        title: 'Hvornår kan det være svært for dig at være i nuet?',
        description:
            'F.eks. når du har undervisning eller når du er sammen med venner',
        dataKeys: [page1WhenIsItHardKey],
    }),
    createQuestionFromConfiguration({
        title: 'Hvad har du tendens til at gøre i de situationer?',
        description: 'F.eks. doomscrolling eller glemmer at lytte efter',
        dataKeys: [page1TypicalActionsKey],
    }),
    createQuestionFromConfiguration({
        title: 'Vælg de situationer, hvor du gerne vil øve dig',
        description:
            'Det kan være godt bare at starte med 1 eller 2 situationer',
        dataKeys: [page2SituationsToPracticeKey],
    }),
];

export function Module2S1Exercise1({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    const [page1TypicalActionsInput, setPage1TypicalActionsInput] =
        useSyncedState(exercise.Data[page1TypicalActionsKey] ?? '');
    const [page1WhenIsItHardInput, setPage1WhenIsItHardInput] = useSyncedState(
        exercise.Data[page1WhenIsItHardKey] ?? '',
    );
    const [page2SituationsToPracticeInput, setPage2SituationsToPracticeInput] =
        useSyncedState(exercise.Data[page2SituationsToPracticeKey] ?? '');

    return (
        <Exercise title="Kortlæg dit nærvær">
            <ProgressBar current={exercise.CurrentPage} total={3} />

            <Switch
                value={exercise.CurrentPage}
                default={1}
                cases={{
                    1: (
                        <Page1
                            page1TypicalActionsInput={page1TypicalActionsInput}
                            onChangePage1TypicalActionsInput={
                                setPage1TypicalActionsInput
                            }
                            page1WhenIsItHardInput={page1WhenIsItHardInput}
                            onChangePage1And2WhenIsItHardInput={
                                setPage1WhenIsItHardInput
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                    data: {
                                        [page1TypicalActionsKey]:
                                            normalizeInput(
                                                page1TypicalActionsInput,
                                            ),
                                        [page1WhenIsItHardKey]: normalizeInput(
                                            page1WhenIsItHardInput,
                                        ),
                                    },
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            page1WhenIsItHardInput={page1WhenIsItHardInput}
                            page2SituationsToPracticeInput={
                                page2SituationsToPracticeInput
                            }
                            onChangeExercise211Page2SituationsToPracticeInput={
                                setPage2SituationsToPracticeInput
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                    data: {
                                        [page2SituationsToPracticeKey]:
                                            normalizeInput(
                                                page2SituationsToPracticeInput,
                                            ),
                                    },
                                });
                            }}
                        />
                    ),
                    3: (
                        <Page3
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                });
                            }}
                        />
                    ),
                }}
            />
        </Exercise>
    );
}

type Page1Props = {
    page1TypicalActionsInput: string;
    onChangePage1TypicalActionsInput: (value: string) => void;
    page1WhenIsItHardInput: string;
    onChangePage1And2WhenIsItHardInput: (value: string) => void;
    onContinue: () => void;
};
function Page1({
    page1TypicalActionsInput,
    onChangePage1TypicalActionsInput,
    page1WhenIsItHardInput,
    onChangePage1And2WhenIsItHardInput,
    onContinue,
}: Page1Props) {
    return (
        <>
            <LabelDisplay2 class="mb-10">
                Hvornår er det svært at være nærværende?
            </LabelDisplay2>
            <LabelBody class="mb-30">
                Som sagt kan det at fokusere på nuet hjælpe til at slippe
                bekymringerne og tankemylderet. For at øve det skal du i denne
                øvelse starte med at kortlægge de situationer, hvor du kan have
                svært ved at være nærværende.
            </LabelBody>
            <Module2S1Exercise1Illustration1 className="mx-auto" />
            <Card class="my-30">
                <LabelDisplay1 class="mb-10">
                    Hvornår kan det være svært for dig at være i nuet?
                </LabelDisplay1>
                <LabelBody>
                    <BulletList
                        items={[
                            'I hvilke situationer kan det være svært? F.eks. når du har undervisning, når du er sammen med venner',
                        ]}
                    />
                </LabelBody>
                <Textarea
                    placeholder="Skriv her..."
                    text={page1WhenIsItHardInput}
                    onChangeText={onChangePage1And2WhenIsItHardInput}
                />
                <LabelBody className="mt-10">
                    <BulletList
                        items={[
                            'Hvad har du tendens til at gøre i de situationer? F.eks. doomscrolling, lægger planer, overtænker, glemmer at lytte efter',
                        ]}
                    />
                </LabelBody>
                <Textarea
                    placeholder="Skriv her..."
                    text={page1TypicalActionsInput}
                    onChangeText={onChangePage1TypicalActionsInput}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

type Page2Props = {
    page1WhenIsItHardInput: string;
    page2SituationsToPracticeInput: string;
    onChangeExercise211Page2SituationsToPracticeInput: (value: string) => void;
    onContinue: () => void;
};
function Page2({
    page1WhenIsItHardInput,
    page2SituationsToPracticeInput,
    onChangeExercise211Page2SituationsToPracticeInput,
    onContinue,
}: Page2Props) {
    return (
        <>
            <LabelDisplay1 class="mb-10">
                Hvornår vil du være mere nærværende?
            </LabelDisplay1>
            <LabelBody>
                Nu har du fået et overblik over, hvor det kan være svært for dig
                at være i nuet.
            </LabelBody>
            <LabelBody>
                I næste del af øvelsen kan du overveje, hvornår du vil øve dig i
                at være mere tilstede. Senere kommer der øvelser, der kan hjælpe
                dig til hvordan.
            </LabelBody>
            {page1WhenIsItHardInput && (
                <>
                    <LabelBodyHeadline
                        color="text-dark-red"
                        class="mb-10 mt-30"
                    >
                        Dine situationer
                    </LabelBodyHeadline>
                    <LabelTelescope class="whitespace-pre-wrap">
                        {page1WhenIsItHardInput}
                    </LabelTelescope>
                </>
            )}
            <Card class="my-30">
                <LabelDisplay1 class="mb-10">
                    Vælg de situationer, hvor du gerne vil øve dig
                </LabelDisplay1>
                <LabelBody>
                    <BulletList
                        items={[
                            'Det kan være godt bare at starte med 1 eller 2 situationer.',
                        ]}
                    />
                </LabelBody>
                <Textarea
                    placeholder="Skriv her..."
                    text={page2SituationsToPracticeInput}
                    onChangeText={
                        onChangeExercise211Page2SituationsToPracticeInput
                    }
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

type Page3Props = {
    onContinue: () => void;
};
function Page3({ onContinue }: Page3Props) {
    return (
        <>
            <LabelDisplay1 class="mb-10">Hvad kan du gøre nu?</LabelDisplay1>
            <LabelBody>
                Nu har du kortlagt, hvor du kan have svært ved at være i nuet,
                og hvad du typisk gør. Når du kommer tilbage til modulet, er der
                tre forskellige øvelser, der kan hjælpe dig til, hvordan du kan
                være mere nærværende.
                <br />
                <br />
                Vi anbefaler, at du prøver dem alle tre af og finder den eller
                dem, som fungerer for dig.
            </LabelBody>
            <SwirlyBox class="my-30">Er du klar på det?</SwirlyBox>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Tilbage til modulet
            </SecondaryButton>
        </>
    );
}
